import React, { useState, useMemo } from "react";
import { List } from "react-window";
import { Table, Braces, Maximize2 } from "lucide-react";
import { toRows, extractColumns } from "./executionUtils";
import ExpandableTable from "./ExpandableTable";
import LogsSection from "./LogsSection";
import ExecutionResultModal from "./ExecutionResultModal";

function JsonLine({ index, style, lines }) {
    return (
        <div style={style} className="flex text-[11px] font-mono">
            <span className="w-10 text-gray-400 text-right pr-2 select-none flex-shrink-0">{index + 1}</span>
            <span className="text-gray-700 whitespace-pre">{lines[index]}</span>
        </div>
    );
}

function JsonView({ result }) {
    const lines = useMemo(() => JSON.stringify(result, null, 2).split("\n"), [result]);
    return (
        <div className="bg-gray-50 rounded border border-gray-200 overflow-hidden">
            <List height={Math.min(300, lines.length * 18)} rowCount={lines.length} rowHeight={18}
                rowComponent={JsonLine} rowProps={{ lines }} />
        </div>
    );
}

const TAB = "px-2 py-0.5 text-[10px] font-medium rounded flex items-center gap-1 transition-colors";

export default function ExecutionDetailPane({ selected, logs }) {
    const [view, setView] = useState("table");
    const [modalOpen, setModalOpen] = useState(false);
    const rows = useMemo(() => selected ? toRows(selected.result) : null, [selected?.result]);
    const columns = useMemo(() => rows ? extractColumns(rows) : null, [rows]);
    if (!selected) return <div className="flex-1 p-3"><div className="text-xs text-gray-400 italic">Select a step to view output</div></div>;

    const itemCount = rows ? rows.length : null;

    return (
        <div className="flex-1 overflow-y-auto p-3 space-y-8">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {selected.result && (
                        <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
                            <button onClick={() => setView("table")} className={`${TAB} ${view === "table" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`}>
                                <Table size={10} /> Table
                            </button>
                            <button onClick={() => setView("json")} className={`${TAB} ${view === "json" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`}>
                                <Braces size={10} /> JSON
                            </button>
                        </div>
                    )}
                    {selected.result && (
                        <button onClick={() => setModalOpen(true)} className="p-1 hover:bg-gray-100 rounded" title="Open fullscreen">
                            <Maximize2 size={12} className="text-gray-400" />
                        </button>
                    )}
                </div>
                {itemCount != null && <span className="text-[10px] text-gray-400">{itemCount} item{itemCount !== 1 ? "s" : ""}</span>}
            </div>
            {selected.result ? (
                view === "table" && rows
                    ? <ExpandableTable rows={rows} columns={columns} />
                    : <JsonView result={selected.result} />
            ) : <div className="text-xs text-gray-400 italic">No output yet</div>}
            <LogsSection logs={logs} />
            {modalOpen && <ExecutionResultModal selected={selected} logs={logs} onClose={() => setModalOpen(false)} />}
        </div>
    );
}
